import { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { RootState, AppDispatch } from "../store";
import { useGetProjectsQuery } from "../store/api/projects";
import { taskApi, useGetProjectTasksQuery } from "../store/api/tasks";
import { useGetWorkspaceMembersQuery } from "../store/api/workspaces";
import { ProjectTask } from "../types";
import { isOverdue } from "../utils/taskGenerator";
import storage from "../utils/storage";
import { STORAGE_KEYS, WORKSPACE_DEFAULT_TIMEZONE } from "../utils/constants";
import { Button } from "../components/ui/button";
import { Loader2, Plus, ChevronDown, FileText, Zap } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import TaskListView from "../components/TaskListView";
import FilterPanel from "../components/FilterPanel";
import CreateRoutineTaskModal from "../components/CreateRoutineTaskModal";
import AddTasksFromTemplateModal from "../components/AddTasksFromTemplateModal";
import QuickAddTaskModal from "../components/QuickAddTaskModal";
import { startOfDay, endOfDay } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";

export default function TasksPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { filters, setFilters } = useApp();
  const { t, language } = useLanguage();
  
  // Get current workspace, user and role from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const currentRole = useSelector((state: RootState) => state.workspace.currentRole);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const workspaceId = currentWorkspace?.id || '';
  const workspaceTimeZone = currentWorkspace?.timezone || WORKSPACE_DEFAULT_TIMEZONE;
  
  // Get role from Redux, localStorage, or user
  const storedRole = storage.get(STORAGE_KEYS.CURRENT_ROLE);
  const userRole = currentRole || storedRole || currentUser?.role;
  const isGuest = userRole === 'guest';
  
  // Extract stable values from currentUser to avoid infinite loops
  const currentUserId = currentUser?.id;
  const currentUserProjectIds = currentUser?.projectIds;

  // Fetch projects from API
  const { data: projects = [], isLoading: isLoadingProjects } = useGetProjectsQuery(
    workspaceId,
    { skip: !workspaceId }
  );

  // Fetch workspace users from API
  const { data: users = [] } = useGetWorkspaceMembersQuery(
    workspaceId,
    { skip: !workspaceId }
  );

  // Get active (non-archived) projects
  // Note: Backend already filters projects for guest users to only return their assigned projects
  const activeProjects = useMemo(() => {
    const nonArchived = projects.filter(p => !p.isArchived);
    
    // Debug logging
    console.log('[TasksPage] User info:', { currentUserId, isGuest, userRole, workspaceId });
    console.log('[TasksPage] Projects from API:', projects.length);
    console.log('[TasksPage] Active (non-archived) projects:', nonArchived.length);
    nonArchived.forEach(p => console.log(`  - ${p.name} (${p.id})`));
    
    return nonArchived;
  }, [projects, isGuest, currentUserId, userRole, workspaceId]);
  
  // Create stable project IDs string for useEffect dependency
  const activeProjectIds = useMemo(() => 
    activeProjects.map(p => p.id).sort().join(','),
    [activeProjects]
  );

  // State to store all tasks from all projects
  const [allProjectTasks, setAllProjectTasks] = useState<ProjectTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  
  // Counter to force refetch (incremented when tasks are updated)
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Function to trigger refetch
  const refetchTasks = useCallback(() => {
    setRefetchCounter(prev => prev + 1);
  }, []);

  // Fetch tasks from all active projects
  useEffect(() => {
    const subscriptions: (() => void)[] = [];
    const projectIdsArray = activeProjectIds ? activeProjectIds.split(',').filter(id => id) : [];
    
    console.log('[TasksPage] Fetching tasks for projects:', projectIdsArray);
    
    const fetchAllTasks = async () => {
      if (!workspaceId || projectIdsArray.length === 0) {
        console.log('[TasksPage] No projects to fetch tasks for (workspaceId:', workspaceId, ', projects:', projectIdsArray.length, ')');
        setAllProjectTasks([]);
        return;
      }

      setIsLoadingTasks(true);
      try {
        const taskPromises = projectIdsArray.map(projectId => {
          console.log('[TasksPage] Fetching tasks for project:', projectId);
          // Use forceRefetch to ensure we get fresh data
          const subscription = dispatch(
            taskApi.endpoints.getProjectTasks.initiate(
              { workspaceId, projectId },
              { forceRefetch: true }
            )
          );
          subscriptions.push(subscription.unsubscribe);
          return subscription.unwrap();
        });

        const results = await Promise.all(taskPromises);
        const combinedTasks = results.flat();
        console.log('[TasksPage] Fetched tasks:', combinedTasks.length, 'tasks from', results.length, 'projects');
        setAllProjectTasks(combinedTasks);
      } catch (error) {
        console.error('[TasksPage] Failed to fetch tasks:', error);
        setAllProjectTasks([]);
      } finally {
        setIsLoadingTasks(false);
      }
    };

    fetchAllTasks();
    
    // Cleanup subscriptions
    return () => {
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, [workspaceId, activeProjectIds, dispatch, refetchCounter]);

  const [isCreateRoutineModalOpen, setIsCreateRoutineModalOpen] = useState(false);
  const [isAddFromTemplateModalOpen, setIsAddFromTemplateModalOpen] = useState(false);
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [showProjectSelectDialog, setShowProjectSelectDialog] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'template' | 'quick' | null>(null);

  // Disable body scroll on this page
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Set default filters on mount
  useEffect(() => {
    setFilters({
      ...filters,
      projectIds: [],
      status: 'all',
      taskType: 'all',
      assigneeIds: [],
      dateRange: { type: 'all' },
    });
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Convert ProjectTask[] to TaskInstance format compatible with TaskListView
  const taskInstances = useMemo(() => {
    console.log('[TasksPage] All project tasks from API:', allProjectTasks.length);
    
    return allProjectTasks.map(task => {
      // Convert date strings to Date objects
      const dueDate = task.dueDate ? new Date(task.dueDate) : new Date();
      const completedAt = task.completedAt ? new Date(task.completedAt) : undefined;
      
      // Create repeatPattern object for TaskListView
      const repeatPattern = task.isRepeatable && task.repeatType !== 'none' ? {
        type: task.repeatType,
        initialDueDate: dueDate,
        useMonthEnd: task.useMonthEnd || false,
      } : undefined;
      
      return {
        ...task,
        dueDate,
        completedAt,
        // Add routineTaskId for backward compatibility
        routineTaskId: task.isRepeatable ? task.id : null,
        // Add repeatPattern directly to the task
        repeatPattern,
        // Ensure occurrenceDate exists
        occurrenceDate: task.createdAt ? new Date(task.createdAt) : new Date(),
      };
    });
  }, [allProjectTasks]);

  // ゲストユーザーの場合、自分のプロジェクトの全タスクを表示
  // Note: activeProjects already filters to only projects where guest is a member
  // so taskInstances only contains tasks from those projects
  const visibleTasks = useMemo(() => {
    // Guest users see all tasks from their assigned projects
    // (activeProjects is already filtered to guest's projects)
    return taskInstances;
  }, [taskInstances]);

  // フィルタークリック処理
  const handleFilterClick = (type: 'all' | 'pending' | 'overdue' | 'completed') => {
    if (type === 'all') {
      // すべてのフィルタをクリア
      setFilters({
        projectIds: [],
        dateRange: { type: 'all' },
        assigneeIds: [],
        status: 'all',
      });
    } else if (type === 'pending') {
      // 未完了のみ表示
      setFilters({
        ...filters,
        dateRange: { type: 'all' },
        status: 'pending',
      });
    } else if (type === 'overdue') {
      // 期限切れのみ表示
      setFilters({
        ...filters,
        dateRange: { type: 'overdue' },
        status: 'pending',
      });
    } else if (type === 'completed') {
      // 完了済みのみ表示
      setFilters({
        ...filters,
        dateRange: { type: 'all' },
        status: 'completed',
      });
    }
  };

  // フィルター適用
  const filteredTasks = useMemo(() => {
    let filtered = visibleTasks;

    // プロジェクトフィルター
    if (filters.projectIds.length > 0) {
      filtered = filtered.filter((task) =>
        filters.projectIds.includes(task.projectId)
      );
    }

    // 期限フィルター - only apply if not 'all'
    if (filters.dateRange.type !== 'all') {
      // カスタム期間
      if (filters.dateRange.customStart || filters.dateRange.customEnd) {
        const start = filters.dateRange.customStart
          ? startOfDay(filters.dateRange.customStart)
          : null;
        const end = filters.dateRange.customEnd
          ? endOfDay(filters.dateRange.customEnd)
          : null;

        filtered = filtered.filter((task) => {
          const taskDue = task.dueDate;
          if (start && end) {
            return taskDue >= start && taskDue <= end;
          } else if (start) {
            return taskDue >= start;
          } else if (end) {
            return taskDue <= end;
          }
          return true;
        });
      }

      // 期限切れフィルター
      if (filters.dateRange.type === "overdue") {
        filtered = filtered.filter(
          (task) => task.status === "pending" && isOverdue(task, workspaceTimeZone)
        );
      }
    }

    // 担当者フィルター
    if (filters.assigneeIds.length > 0) {
      filtered = filtered.filter((task) =>
        task.assigneeIds.some((id) => filters.assigneeIds.includes(id))
      );
    }

    // ステータスフィルター
    if (filters.status !== "all") {
      filtered = filtered.filter((task) => task.status === filters.status);
    }

    // タスクタイプフィルター
    if (filters.taskType === 'recurring') {
      filtered = filtered.filter((task) => task.routineTaskId !== null);
    } else if (filters.taskType === 'oneTime') {
      filtered = filtered.filter((task) => task.routineTaskId === null);
    }

    // 期限日でソート（期限が近い順）
    return filtered.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
  }, [visibleTasks, filters, workspaceTimeZone]);

  // 統計情報
  const stats = useMemo(() => {
    const pending = visibleTasks.filter((t) => t.status === "pending");
    const overdue = pending.filter((t) => isOverdue(t, workspaceTimeZone));
    const recurring = visibleTasks.filter((t) => t.routineTaskId !== null);
    const oneTime = visibleTasks.filter((t) => t.routineTaskId === null);

    return {
      pending: pending.length,
      overdue: overdue.length,
      recurring: recurring.length,
      oneTime: oneTime.length,
    };
  }, [visibleTasks, workspaceTimeZone]);

  // テンプレートから追加ボタンのハンドラー
  const handleAddFromTemplate = () => {
    if (activeProjects.length === 0) {
      return;
    }
    setPendingAction('template');
    setShowProjectSelectDialog(true);
  };

  // クイック追加ボタンのハンドラー
  const handleQuickAdd = () => {
    if (activeProjects.length === 0) {
      return;
    }
    setPendingAction('quick');
    setShowProjectSelectDialog(true);
  };

  // Get project members for selected project
  const selectedProject = activeProjects.find(p => p.id === selectedProjectId);
  const selectedProjectMembers = useMemo(() => {
    if (!selectedProject) return [];
    return users.filter(u => selectedProject.memberIds?.includes(u.id));
  }, [selectedProject, users]);

  const isLoading = isLoadingProjects || isLoadingTasks;

  return (
    <div className="h-[calc(100vh-73px)] flex flex-col overflow-hidden">
      {/* Header section - fixed */}
      <div className="flex-shrink-0 max-w-[1600px] mx-auto w-full px-6 pt-6 pb-2">
        <p className="text-gray-600 text-sm">
          {t('tasks.completionTriggerDesc')}
        </p>
      </div>

      {/* Show filter panel for non-guests OR guests with multiple projects */}
      {(!isGuest || activeProjects.length > 1) ? (
        <div className="flex-1 min-h-0 max-w-[1600px] mx-auto w-full px-6 pb-6">
          <div className="grid grid-cols-[280px_1fr] gap-6 h-full">
            {/* フィルターパネル - 独立スクロール */}
            <div className="overflow-y-auto h-full pr-2">
              <FilterPanel projects={activeProjects} />
            </div>

            {/* タスクリスト - 独立スクロール */}
            <div className="overflow-y-auto h-full">
              <div className="mb-3">
                <p className="text-gray-600 text-sm">
                  {filteredTasks.length}{t('common.count')}{language === 'ja' ? 'のタスクを表示中' : ' tasks shown'}
                </p>
              </div>
              {isLoading ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">{t('common.loading') || 'Loading tasks...'}</p>
                </div>
              ) : (
                <TaskListView tasks={filteredTasks} users={users} onTaskUpdate={refetchTasks} />
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Guest user with single project - no filter panel */
        <div className="flex-1 min-h-0 overflow-y-auto max-w-[1600px] mx-auto w-full px-6 pb-6">
          <div className="mb-3">
            <p className="text-gray-600 text-sm">
              {filteredTasks.length}{t('common.count')}{language === 'ja' ? 'のタスクを表示中' : ' tasks shown'}
            </p>
          </div>
          {isLoading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">{t('common.loading') || 'Loading tasks...'}</p>
            </div>
          ) : (
            <TaskListView tasks={filteredTasks} users={users} onTaskUpdate={refetchTasks} />
          )}
        </div>
      )}

      {/* タスク作成モーダル */}
      {isCreateRoutineModalOpen && (
        <CreateRoutineTaskModal
          onClose={() => setIsCreateRoutineModalOpen(false)}
        />
      )}

      {/* タスクテンプレートから追加モーダル */}
      {selectedProjectId && (
        <AddTasksFromTemplateModal
          open={isAddFromTemplateModalOpen}
          onOpenChange={setIsAddFromTemplateModalOpen}
          projectId={selectedProjectId}
        />
      )}

      {/* タスククイック追加モーダル */}
      {selectedProjectId && (
        <QuickAddTaskModal
          open={isQuickAddModalOpen}
          onOpenChange={setIsQuickAddModalOpen}
          projectId={selectedProjectId}
          projectMembers={selectedProjectMembers}
        />
      )}

      {/* プロジェクト選択ダイアログ */}
      {showProjectSelectDialog && (
        <Dialog
          open={showProjectSelectDialog}
          onOpenChange={setShowProjectSelectDialog}
        >
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{t('tasks.selectProject')}</DialogTitle>
              <DialogDescription>
                {t('tasks.selectProjectDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {activeProjects.map(project => (
                <div key={project.id} className="flex items-center">
                  <input
                    id={project.id}
                    type="radio"
                    value={project.id}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    checked={selectedProjectId === project.id}
                    onChange={() => setSelectedProjectId(project.id)}
                  />
                  <Label
                    htmlFor={project.id}
                    className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    {project.name}
                  </Label>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (selectedProjectId) {
                    if (pendingAction === 'template') {
                      setIsAddFromTemplateModalOpen(true);
                    } else if (pendingAction === 'quick') {
                      setIsQuickAddModalOpen(true);
                    }
                    setShowProjectSelectDialog(false);
                  }
                }}
              >
                {t('common.next')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
